import { useEffect } from 'react';

import { useNodeDetail } from 'api/data/hooks/useNodeDetail';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

/**
 * Whether the panel can show its node. A node that can't be loaded (deleted, or its type removed from the
 * blueprint) calls `onUnavailable` with a snackbar instead of leaving an empty panel for the user to dismiss.
 */
export function useNodeAvailability(
  projectId: string,
  templateNodeId: string,
  dataNodeId: string,
  proposalId: string | undefined,
  onUnavailable: () => void
) {
  // Same query key as the panel's own, so React Query shares the one request.
  const { data, isError } = useNodeDetail(projectId, templateNodeId, dataNodeId, proposalId);
  // `undefined` until the blueprint has loaded, which a direct load of the details URL can beat.
  const hasNodeType = SELECTED_PROJECT_STORE((state) =>
    state.template?.nodes.some((node) => node.id === templateNodeId)
  );
  // A failed background refetch keeps the loaded node, so only an error with nothing loaded means it is gone.
  // Any failed first load (500, network) also reads as gone, because `api/fetcher.ts` drops the HTTP
  // status. Carry the status in `OutputOperationResult` and gate on 404 if that misleads users.
  const isUnavailable = (isError && !data) || hasNodeType === false;

  useEffect(() => {
    if (!isUnavailable) return;
    SNACKBAR_STORE.getState().showSnackbar('This node is no longer available.', 'warning', true);
    onUnavailable();
  }, [isUnavailable, onUnavailable]);

  return { isShown: !isUnavailable && !!hasNodeType };
}
